// src/pages/CreateTaxpayer.jsx
import React, { useState } from "react";
import LocationService from "../../services/locationService";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row, Button, Card, Form, ProgressBar, Alert, Spinner } from '@themesberg/react-bootstrap';
import taxpayerService from "../../services/taxpayerService";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
 

const CreateTaxpayer = () => {
  const { id } = useParams(); // Get taxpayer ID from URL
  const isEdit = Boolean(id); // Determine if we're in edit mode

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [hiddenAlerts, setHiddenAlerts] = useState([]);
  const [formData, setFormData] = useState({
    // Paso 1: Información Personal
    identification_type: "01",
    identification: "206520952",
    email: "narvaezeliecer@gmail.com",
    phone_country_code: "",
    cellphone: "84309102",
    comercial_name: "",
    is_active: true,

    // Paso 2: Información de Ubicación
    province_id: "",
    canton_id: "",
    district_id: "",
    neighborhood: "",
    address: "",

    // Paso 3: Credenciales de Hacienda
    username_hacienda: "",
    password_hacienda: "",
    pin_hacienda: "",
    key: null
  });

  // Estados para las listas desplegables
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cantons, setCantons] = useState([]);
  const [districts, setDistricts] = useState([]);


  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Limpiar error del campo al cambiar
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }

    // Lógica para cargar datos dependientes de ubicación
    if (name === 'province_id') {
      loadCantons(value);
      setFormData({
        ...formData,
        [name]: value,
        canton_id: "",
        district_id: "",
        neighborhood: ""
      });
    } else if (name === 'canton_id') {
      loadDistricts(value);
      setFormData({
        ...formData,
        [name]: value,
        district_id: "",
        neighborhood: ""
      });
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({ ...formData, [name]: files[0] || null });

    // Limpiar error del campo al cambiar
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleStatusToggle = async (newStatus) => {
    if (!isEdit || !id) {
      // Si no estamos en modo edición, solo actualizar el estado local
      setFormData({ ...formData, is_active: newStatus });
      return;
    }

    try {
      setLoading(true);
      const result = await taxpayerService.updateTaxpayerStatus(id, newStatus);
      
      if (result.success) {
        setFormData({ ...formData, is_active: newStatus });
        setAlertMessage(result.message);
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error updating taxpayer status:", error);
      setAlertMessage(error.message || "Error al actualizar el estado del contribuyente");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const onClose = (alertId) => {
    const hiddenAlertsUpdated = [...hiddenAlerts, alertId];
    setHiddenAlerts(hiddenAlertsUpdated);
  };

  const shouldShowAlert = (alertId) => (
    hiddenAlerts.indexOf(alertId) === -1
  );

  // Funciones para cargar datos usando el service
  const loadProvinces = async () => {
    try {
      const data = await LocationService.getProvinces();
      setProvinces(data);
    } catch (error) {
      console.error("Error loading provinces:", error);
    }
  };

  const loadCantons = async (provinceId) => {
    try {
      const data = await LocationService.getCantonsByProvince(provinceId);
      setCantons(data);
    } catch (error) {
      console.error("Error loading cantons:", error);
    }
  };

  const loadDistricts = async (cantonId) => {
    try {
      const data = await LocationService.getDistrictsByCanton(cantonId);
      setDistricts(data);
    } catch (error) {
      console.error("Error loading districts:", error);
    }
  };


  const loadCountries = async () => {
    try {
      const data = await LocationService.getCountries();
      setCountries(data);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };


  // Load taxpayer data for editing
  const loadTaxpayerData = async (taxpayerId) => {
    try {
      setLoading(true);
      const response = await taxpayerService.getById(taxpayerId);

      if (response.success && response.data.taxpayer) {
        const { taxpayer } = response.data;
        const { profile } = taxpayer;

        // Populate form with existing data
        setFormData({
          // Personal info
          identification_type: profile.identification_type || "",
          identification: profile.identification || "",
          email: taxpayer.email || "",
          phone_country_code: profile.phone_country_code || "",
          cellphone: profile.cellphone || "",
          comercial_name: profile.comercial_name || "",
          is_active: taxpayer.is_active !== undefined ? taxpayer.is_active : true,

          // Location info
          province_id: profile.province?.id || "",
          canton_id: profile.canton?.id || "",
          district_id: profile.district?.id || "",
          neighborhood: profile.neighborhood || "",
          address: profile.address || "",

          // Hacienda credentials
          username_hacienda: profile.username_hacienda || "",
          password_hacienda: "", // Don't pre-fill password for security
          pin_hacienda: "", // Don't pre-fill PIN for security
          key: null, // File uploads need to be re-selected
          sector_id: profile.sector?.id || "",
          regime_id: profile.regime?.id || ""
        });

        // Load dependent data based on existing selections
        if (profile.province?.id) {
          loadCantons(profile.province.id);
        }
        if (profile.canton?.id) {
          loadDistricts(profile.canton.id);
        }
      }
    } catch (error) {
      console.error("Error loading taxpayer data:", error);
      setAlertMessage("Error al cargar los datos del contribuyente");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Reset validation when step changes
  React.useEffect(() => {
    setErrors({});
    setShowValidation(false);
  }, [currentStep]);

  // Cargar datos iniciales
  React.useEffect(() => {
    loadCountries();
    loadProvinces();

    // Load taxpayer data if in edit mode
    if (isEdit && id) {
      loadTaxpayerData(id);
    }
  }, [isEdit, id]);

  const validateCurrentStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.identification) newErrors.identification = "La identificación es requerida";
      if (!formData.email) newErrors.email = "El email es requerido";
      if (!formData.cellphone) newErrors.cellphone = "El teléfono es requerido";
      if (!formData.phone_country_code) newErrors.phone_country_code = "El país es requerido";

      if (!formData.comercial_name) newErrors.comercial_name = "El nombre comercial es requerido";

    } else if (currentStep === 2) {
      if (!formData.province_id) newErrors.province_id = "La provincia es requerida";
      if (!formData.canton_id) newErrors.canton_id = "El cantón es requerido";
      if (!formData.district_id) newErrors.district_id = "El distrito es requerido";
      if (!formData.address) newErrors.address = "La dirección es requerida";
    } else if (currentStep === 3) {
      // Only require hacienda fields when creating (not editing)
      if (!isEdit) {
        if (!formData.username_hacienda) newErrors.username_hacienda = "El usuario de Hacienda es requerido";
        if (!formData.password_hacienda) newErrors.password_hacienda = "La contraseña de Hacienda es requerida";
        if (!formData.pin_hacienda) newErrors.pin_hacienda = "El PIN de Hacienda es requerido";
        if (!formData.key) newErrors.key = "El archivo de firma digital es requerido";
      }
    
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    setShowValidation(true);
    if (validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Validar paso actual
    setShowValidation(true);
    if (!validateCurrentStep()) {
      return;
    }

    try {
      // Mostrar loading state
      setLoading(true);

      // Preparar FormData
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        const value = formData[key];

        // Si es el campo de archivo, solo enviarlo si es realmente un File
        if (key === 'key') {
          if (value instanceof File) {
            formDataToSend.append('key', value, value.name);
          }
          // No agregar el campo 'key' si no es un archivo para evitar errores de validacion
        } else if (value !== null && value !== "") {
          formDataToSend.append(key, value);
        }
      });

      // Use appropriate service method based on mode
      let result;
      if (isEdit) {
        result = await taxpayerService.update(id, formDataToSend);
      } else {
        result = await taxpayerService.register(formDataToSend);
      }

      if (result.success) {
        // Mostrar mensaje de éxito
        setAlertMessage(isEdit ? "Contribuyente actualizado exitosamente" : "Contribuyente creado exitosamente");
        setShowAlert(true);

        // Redirigir
        navigate("/taxpayers");
      }

    } catch (error) {
      console.error("Error creating taxpayer:", error);

      // Mostrar el alert con el mensaje de error
      setAlertMessage(error.message || "Error inesperado");


    } finally {
      setLoading(false);
    }
  };


  const renderError = (fieldName) => {
    if (showValidation && errors[fieldName]) {
      return <div className="text-danger small mt-1">{errors[fieldName]}</div>;
    }
    return null;
  };

  const renderPersonalInfo = () => (
    <>
      <h5 className="mb-4">Información Personal</h5>
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group className="mb-2">
            <Form.Label>Tipo identificación</Form.Label>
            <Form.Select
              name="identification_type"
              value={formData.identification_type}
              onChange={handleChange}
              isInvalid={showValidation && !!errors.identification_type}
            >
              <option value="01">Cédula</option>
              <option value="02">Cédula Jurídica </option>\
              <option value="03">DIMEX</option>
              <option value="04">NITE</option>
              <option value="03">Extranjero No Domiciliado</option>
              <option value="05">No Contribuyente</option>
            </Form.Select>
            {renderError('identification_type')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Identificación *</Form.Label>
            <Form.Control
              type="text"
              name="identification"
              value={formData.identification}
              onChange={handleChange}
              placeholder="Ingrese su identificación"
              isInvalid={showValidation && !!errors.identification}
            />
            {renderError('identification')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Nombre Comercial *</Form.Label>
            <Form.Control
              type="text"
              name="comercial_name"
              value={formData.comercial_name}
              onChange={handleChange}
              placeholder="Nombre o razón social del contribuyente"
              isInvalid={showValidation && !!errors.comercial_name}
            />
            {renderError('comercial_name')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Email *</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="nombre@empresa.com"
              isInvalid={showValidation && !!errors.email}
            />
            {renderError('email')}
          </Form.Group>
        </Col>
      </Row>


      <Row>
        <Col md={6} className="mb-3">
          <Form.Group className="mb-2">
            <Form.Label>País *</Form.Label>
            <Form.Select
              name="phone_country_code"
              value={formData.phone_country_code}
              onChange={handleChange}
              isInvalid={showValidation && !!errors.phone_country_code}
            >
              <option value="">Seleccione un país</option>
              {countries.map(country => (
                <option key={country.id} value={country.phonecode}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            {renderError('phone_country_code')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Teléfono *</Form.Label>
            <Form.Control
              type="text"
              name="cellphone"
              value={formData.cellphone}
              onChange={handleChange}
              placeholder="8888-8888"
              isInvalid={showValidation && !!errors.cellphone}
            />
            {renderError('cellphone')}
          </Form.Group>
        </Col>
      </Row>

      {isEdit && (
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>Estado del Contribuyente</Form.Label>
              <div className="d-flex align-items-center mt-2">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  checked={formData.is_active}
                  onChange={(e) => handleStatusToggle(e.target.checked)}
                  disabled={loading}
                  label={formData.is_active ? "Activo" : "Inactivo"}
                  className={`me-3 ${formData.is_active ? 'text-success' : 'text-danger'}`}
                />
                {loading && <Spinner as="span" animation="border" size="sm" />}
              </div>
              <Form.Text className="text-muted">
                {formData.is_active 
                  ? "El contribuyente está activo y puede realizar operaciones" 
                  : "El contribuyente está inactivo y no puede realizar operaciones"}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      )}

    </>
  );

  const renderLocationInfo = () => (
    <>
      <h5 className="mb-4">Información de Ubicación</h5>
      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Provincia *</Form.Label>
            <Form.Select
              name="province_id"
              value={formData.province_id}
              onChange={handleChange}
              isInvalid={showValidation && !!errors.province_id}
            >
              <option value="">Seleccione una provincia</option>
              {provinces.map(province => (
                <option key={province.id} value={province.id}>
                  {province.name}
                </option>
              ))}
            </Form.Select>
            {renderError('province_id')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Cantón *</Form.Label>
            <Form.Select
              name="canton_id"
              value={formData.canton_id}
              onChange={handleChange}
              disabled={!formData.province_id}
              isInvalid={showValidation && !!errors.canton_id}
            >
              <option value="">Seleccione un cantón</option>
              {cantons.map(canton => (
                <option key={canton.id} value={canton.id}>
                  {canton.name}
                </option>
              ))}
            </Form.Select>
            {renderError('canton_id')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Distrito *</Form.Label>
            <Form.Select
              name="district_id"
              value={formData.district_id}
              onChange={handleChange}
              disabled={!formData.canton_id}
              isInvalid={showValidation && !!errors.district_id}
            >
              <option value="">Seleccione un distrito</option>
              {districts.map(district => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </Form.Select>
            {renderError('district_id')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Barrio</Form.Label>
            <Form.Control
              type="text"
              name="neighborhood"
              value={formData.neighborhood}
              onChange={handleChange}
              placeholder="Ingrese el nombre del barrio"
              isInvalid={showValidation && !!errors.neighborhood}
            />
            {renderError('neighborhood')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Dirección Exacta *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Ingrese la dirección exacta (100 metros norte de la iglesia, casa amarilla...)"
              isInvalid={showValidation && !!errors.address}
            />
            {renderError('address')}
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const renderHaciendaCredentials = () => (
    <>
      <h5 className="mb-4">Información de Hacienda</h5>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Usuario Hacienda *</Form.Label>
            <Form.Control
              type="text"
              name="username_hacienda"
              value={formData.username_hacienda}
              onChange={handleChange}
              placeholder="Usuario para Hacienda Virtual"
              isInvalid={showValidation && !!errors.username_hacienda}
            />
            {renderError('username_hacienda')}
          </Form.Group>
        </Col>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>Contraseña Hacienda {!isEdit ? '*' : ''}</Form.Label>
            <Form.Control
              type="password"
              name="password_hacienda"
              value={formData.password_hacienda}
              onChange={handleChange}
              placeholder="Contraseña para Hacienda Virtual"
              isInvalid={showValidation && !!errors.password_hacienda}
            />
            {renderError('password_hacienda')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label>PIN Hacienda {!isEdit ? '*' : ''}</Form.Label>
            <Form.Control
              type="text"
              name="pin_hacienda"
              value={formData.pin_hacienda}
              onChange={handleChange}
              placeholder="PIN para Hacienda Virtual"
              isInvalid={showValidation && !!errors.pin_hacienda}
            />
            {renderError('pin_hacienda')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12} className="mb-3">
          <Form.Group>
            <Form.Label>Firma Digital de Hacienda {!isEdit ? '*' : ''}</Form.Label>
            <Form.Control
              type="file"
              name="key"
              onChange={handleFileChange}
              accept=".p12"
              isInvalid={showValidation && !!errors.key}
            />
            <Form.Text className="text-muted">
              Seleccione el archivo de firma digital (.p12)
            </Form.Text>
            {renderError('key')}
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Alert variant="info">
            <Alert.Heading>Información Importante</Alert.Heading>
            <p>
              • <strong>Usuario y Contraseña:</strong> Credenciales opcionales para la integración con Hacienda Virtual.
            </p>
            <p className="mb-0">
              • <strong>Firma Digital:</strong> Archivo opcional de certificado digital para firmar documentos electrónicos.
            </p>
          </Alert>
        </Col>
      </Row>
    </>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderPersonalInfo();
      case 2:
        return renderLocationInfo();
      case 3:
        return renderHaciendaCredentials();
      default:
        return null;
    }
  };

  const getProgressPercentage = () => {
    return (currentStep / 3) * 100;
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Información Personal";
      case 2:
        return "Información de Ubicación";
      case 3:
        return "Información de Hacienda";
      default:
        return "";
    }
  };

  return (
    <div>
      <Row>
        <Col xs={12} xl={8}>
          <Card border="light" className="bg-white shadow-sm mb-4">
            <Card.Body>
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-bold">Paso {currentStep} de 3: {getStepTitle()}</span>
                  <span className="text-muted">{Math.round(getProgressPercentage())}% completado</span>
                </div>
                <ProgressBar now={getProgressPercentage()} className="mb-3" />
              </div>

              <Form onSubmit={handleSubmit}>

                <Alert
                  variant="danger"
                  show={alertMessage && shouldShowAlert("danger")}
                  onClose={() => onClose("danger")}>

                  <div className="d-flex justify-content-between">
                    <div>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="icon icon-xs me-1" />
                      <strong>Error:</strong> {alertMessage}
                    </div>
                    <Button variant="close" size="sm" onClick={() => onClose("danger")} />
                  </div>
                </Alert>
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-gray-600"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    ← Anterior
                  </Button>

                  {currentStep < 3 ? (
                    <Button
                      variant="primary"
                      onClick={nextStep}
                    >
                      {loading ? "Validando..." : "Siguiente →"}
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                          {isEdit ? "Actualizando..." : "Creando..."}
                        </>
                      ) : (
                        isEdit ? "✓ Actualizar Contribuyente" : "✓ Crear Contribuyente"
                      )}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreateTaxpayer;